import React, { useEffect, useState } from 'react';
import { View } from 'react-native';

import { MEASUREMENT_UNIT_SHORT_NAME } from '../../../common/constants';
import { Dialog, ErrorCallout, Text } from '../../components';
import { PrimaryButton, SecondaryButton } from '../../design/components';
import { InputNumber, TextInput } from '../../components/input';
import { useThemedStyles } from '../../hooks/useThemedStyles';

const PARLEVEL = 'PARLEVEL';
const CAPACITY = 'CAPACITY';
const MAX_SUGGESTED_VALUE = 99;

const ReportIncorrectStockSettingDialog = ({
	isOpen,
	planogramItem,
	showCapacity,
	reportedSettings,
	submitting,
	onClose,
	onSubmit,
}) => {
	const styles = useThemedStyles(createStyles);
	const [setting, setSetting] = useState(PARLEVEL);
	const [suggestedValue, setSuggestedValue] = useState(null);
	const [detail, setDetail] = useState('');
	const [error, setError] = useState(null);

	const parReported = Boolean(reportedSettings?.[PARLEVEL]);
	// Offered whenever the tenant uses capacities, including a selection with no capacity set yet.
	const capacityAvailable = Boolean(showCapacity);
	const capacityReported = Boolean(reportedSettings?.[CAPACITY]);
	const bothReported = parReported && (!capacityAvailable || capacityReported);

	useEffect(() => {
		if (!isOpen) {
			return;
		}

		// Default to whichever setting still needs a report.
		setSetting(parReported && capacityAvailable ? CAPACITY : PARLEVEL);
		setSuggestedValue(null);
		setDetail('');
		setError(null);
	}, [isOpen, parReported, capacityAvailable]);

	const settingReported = setting === CAPACITY ? capacityReported : parReported;
	const currentValue = setting === CAPACITY ? planogramItem?.capacity : planogramItem?.parlevel;
	const hasCurrentValue = currentValue !== null && currentValue !== undefined;

	const onPressSubmit = async () => {
		setError(null);

		if (settingReported) {
			setError('You have already reported this setting for this product.');
			return;
		}

		if (suggestedValue !== null && suggestedValue !== undefined && suggestedValue !== '') {
			const numericSuggestedValue = Number(suggestedValue);
			if (!Number.isInteger(numericSuggestedValue) || numericSuggestedValue < 0 || numericSuggestedValue > MAX_SUGGESTED_VALUE) {
				setError(`Enter a suggested value between 0 and ${MAX_SUGGESTED_VALUE}, or leave it empty.`);
				return;
			}
		}

		try {
			await onSubmit({
				setting,
				suggestedValue: suggestedValue === null || suggestedValue === '' ? null : Number(suggestedValue),
				detail: detail.trim() || null,
			});
		}
		catch (submitError) {
			setError(submitError?.message || 'Unable to notify the manager. Please try again.');
		}
	};

	return (
		<Dialog
			isOpen={isOpen}
			onClose={onClose}
			shouldShowCloseIcon={!submitting}
			enableCloseDialog={!submitting}
		>
			<View style={styles.container}>
				<Text style={styles.title}>Report incorrect setting</Text>
				<Text style={styles.subtitle}>
					{planogramItem?.product?.name ? `${planogramItem.product.name}${planogramItem.selection ? ` (selection ${planogramItem.selection})` : ''}` : 'This product'}
				</Text>

				{bothReported ? (
					<ErrorCallout text="You have already reported both the par level and the capacity for this product." />
				) : (
					<>
						<Text style={styles.fieldLabel}>What is wrong?</Text>
						<View style={styles.settingRow}>
							<SecondaryButton
								style={[styles.settingButton, setting === PARLEVEL && !parReported && styles.settingButtonSelected]}
								disabled={parReported}
								label={parReported ? 'Par level reported' : 'Par level'}
								onPress={() => {
									setSetting(PARLEVEL);
									setError(null);
								}}
							/>
							{
								capacityAvailable && (
									<SecondaryButton
										style={[styles.settingButton, setting === CAPACITY && !capacityReported && styles.settingButtonSelected]}
										disabled={capacityReported}
										label={capacityReported ? 'Capacity reported' : 'Capacity'}
										onPress={() => {
											setSetting(CAPACITY);
											setError(null);
										}}
									/>
								)
							}
						</View>
						<Text style={styles.hint}>
							Current {setting === CAPACITY ? 'capacity' : 'par level'}: {hasCurrentValue ? currentValue : 'not set'}
							{hasCurrentValue && planogramItem?.product ? ` ${MEASUREMENT_UNIT_SHORT_NAME[planogramItem.product.measurementUnit] || ''}` : ''}
						</Text>

						<View style={styles.field}>
							<Text style={styles.fieldLabel}>Suggested value (optional)</Text>
							<InputNumber
								value={suggestedValue}
								placeholder="e.g. 8"
								nullable={true}
								onChange={value => setSuggestedValue(value)}
							/>
						</View>

						<View style={styles.field}>
							<Text style={styles.fieldLabel}>Details (optional)</Text>
							<TextInput
								value={detail}
								placeholder="What looks wrong?"
								multiline={true}
								numberOfLines={3}
								onChange={value => setDetail(value)}
							/>
						</View>

						{error && (
							<View style={styles.error}>
								<ErrorCallout text={error} />
							</View>
						)}

						<View style={styles.actions}>
							<SecondaryButton
								disabled={submitting}
								label="Cancel"
								onPress={onClose}
							/>
							<PrimaryButton
								style={styles.action}
								size="compact"
								disabled={settingReported}
								loading={submitting}
								label="Notify manager"
								onPress={onPressSubmit}
							/>
						</View>
					</>
				)}
			</View>
		</Dialog>
	);
};

const createStyles = ({ ui: { colors, type, space } }) => ({
	container: {
		minWidth: 280,
	},
	title: {
		...type.cardTitle,
		color: colors.text,
		marginBottom: space.xs,
		// Keep clear of the dialog's close button.
		paddingRight: 36,
	},
	subtitle: {
		...type.bodySmall,
		color: colors.textSecondary,
		marginBottom: space.md,
	},
	field: {
		marginTop: space.md,
	},
	fieldLabel: {
		...type.fieldLabel,
		color: colors.text,
		marginBottom: space.xs,
	},
	settingRow: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		gap: space.sm,
	},
	settingButton: {
		flexGrow: 1,
	},
	settingButtonSelected: {
		borderColor: colors.currentBorder,
		backgroundColor: colors.current,
	},
	hint: {
		...type.secondary,
		color: colors.textSecondary,
		marginTop: 6,
	},
	error: {
		marginTop: space.md,
	},
	actions: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.sm,
		marginTop: space.lg,
	},
	action: {
		flex: 1,
	},
});

export default React.memo(ReportIncorrectStockSettingDialog);
